// src/ui/AvatarCreatorModal.jsx — make (or edit) your avatar, in the browser.
//
// The phone's flow (screens/AvatarCreatorScreen.tsx), step for step: who →
// one selfie (webcam or an uploaded photo; used for the skin tone only, read
// on this machine from a 64-pixel thumbnail and then dropped) → the builder
// (the figure live above tabs: look, skin, eyes, hair, top, bottom, shoes,
// hat — every tap re-assembles it from our own catalogue) → the result (full
// body, rollable, and the portrait that becomes the profile picture, rendered
// here and uploaded). Nothing is written until "Use this avatar".

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Avatar3D from './Avatar3D';
import { X, Camera, Check } from './icons';
import { hasAvatar, previewDoc } from '../services/avatar3d';
import {
  defaultSpec, normalizeSpec, stylesAvailable, skinToneFromPixels, nearestSwatch,
  SKIN_TONES, HAIR_COLORS, CLOTH_COLORS, EYE_COLORS, FACES,
  TOP_STYLES, BOTTOM_STYLES, SHOE_STYLES, HAT_STYLES, HAIR_STYLES,
} from '../services/avatarKit';
import { POSE_IDS } from '../services/avatarPose';
import { FACE_SLIDERS, withFaceDim, parseFaceReading, readingIsUsable } from '../services/avatarFace';
import { BODY_SLIDERS, withBodyDim } from '../services/avatarBody';
import { parseOutfitReading, outfitIsUsable, OUTFIT_MAX_CHARS } from '../services/avatarStyle';
import { applyOutfit } from '../services/avatarStyleApply';
import { applyFaceReading } from '../services/avatarFaceApply';
import { BACKEND_URL } from '../services/socket';
import { auth } from '../config/firebase';
import { getCatalog, warmSpec } from '../services/avatarKitLoader';
import { saveAvatarToProfile } from '../services/avatarProfile';

const TABS = ['pose', 'face', 'body', 'style', 'look', 'skin', 'eyes', 'hair', 'top', 'bottom', 'shoes', 'hat'];
const TAB_LABEL = { pose: 'Pose', face: 'Face', body: 'Body', style: 'Style', look: 'Look', skin: 'Skin', eyes: 'Eyes', hair: 'Hair', top: 'Top', bottom: 'Bottom', shoes: 'Shoes', hat: 'Hat' };
const STYLE_LABEL = {
  none: 'None', buzz: 'Buzz', short: 'Short', bangs: 'Bangs', medium: 'Medium', bob: 'Bob', long: 'Long', curly: 'Curly', bun: 'Bun', ponytail: 'Ponytail',
  tank: 'Tank', tee: 'T-shirt', longsleeve: 'Long sleeve', sweater: 'Sweater', jacket: 'Jacket',
  jeans: 'Jeans', chino: 'Chinos', shorts: 'Shorts', joggers: 'Joggers', 'skirt-short': 'Short skirt', 'skirt-long': 'Long skirt',
  sneakers: 'Sneakers', boots: 'Boots', slides: 'Slides', cap: 'Cap', beanie: 'Beanie', bucket: 'Bucket hat',
  relaxed: 'Relaxed', hip: 'Hand on hip', hero: 'Hands on hips', crossed: 'Arms folded', behind: 'Hands behind', wave: 'Waving', think: 'Thinking',
};
export const EYE_SWATCH = { brown: '#5A3A22', hazel: '#8A6A3A', blue: '#4C7FB8', green: '#5E8B5A', grey: '#8A9099', dark: '#2A1C14' };
const BUILD_STEPS = [-1, -0.5, 0, 0.5, 1];
const FACE_LABEL = {
  headRound: ['Face shape', 'Oval', 'Round'], jawWidth: ['Jaw', 'Narrow', 'Wide'], eyeSize: ['Eyes', 'Small', 'Large'],
  noseWidth: ['Nose', 'Narrow', 'Wide'], mouthWidth: ['Mouth', 'Small', 'Wide'], lipUpper: ['Lips', 'Thin', 'Full'],
};

const BODY_LABEL = {
  shoulders: ['Shoulders', 'Narrow', 'Broad'], chest: ['Chest', 'Narrow', 'Broad'], waist: ['Waist', 'Narrow', 'Thick'],
  muscle: ['Build', 'Soft', 'Defined'], belly: ['Stomach', 'Flat', 'Round'], legs: ['Legs', 'Slender', 'Thick'],
};

/** a sentence, read by our server; null when it cannot be — see the phone's styleAsk.ts */
async function askOutfit(text, options) {
  const u = auth.currentUser;
  const words = String(text || '').trim().slice(0, OUTFIT_MAX_CHARS);
  if (!u || words.length < 2) return null;
  try {
    const idToken = await u.getIdToken();
    const res = await fetch(`${BACKEND_URL}/api/avatar/outfit`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${idToken}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ text: words }),
    });
    if (!res.ok) return null;
    const j = await res.json();
    const reading = parseOutfitReading(j && j.outfit, options);
    return outfitIsUsable(reading) ? reading : null;
  } catch {
    return null;
  }
}

/** the selfie, read by our server; null when it cannot be (the tone path still works) */
async function readFace(dataUrl) {
  const u = auth.currentUser;
  if (!u) return null;
  try {
    const idToken = await u.getIdToken();
    const res = await fetch(`${BACKEND_URL}/api/avatar/face`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${idToken}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ image: dataUrl }),
    });
    if (!res.ok) return null;
    const j = await res.json();
    const reading = parseFaceReading(j && j.reading);
    return readingIsUsable(reading) ? reading : null;
  } catch { return null; }
}

/** the frame as a 512-px JPEG data url — what the model is sent */
function frameDataUrl(src, w, h, mirrored = false) {
  const side = Math.min(w, h);
  const c = document.createElement('canvas');
  c.width = 512; c.height = 512;
  const ctx = c.getContext('2d');
  if (mirrored) { ctx.translate(512, 0); ctx.scale(-1, 1); }
  ctx.drawImage(src, (w - side) / 2, (h - side) / 2, side, side, 0, 0, 512, 512);
  return c.toDataURL('image/jpeg', 0.8);
}

/** the skin tone from an image source (a <video> or an <img>), sampled at 64 px */
export function toneFromSource(src, w, h, mirrored = false) {
  const c = document.createElement('canvas');
  c.width = 64; c.height = 64;
  const ctx = c.getContext('2d');
  if (mirrored) { ctx.translate(64, 0); ctx.scale(-1, 1); }
  // centre crop to a square before the downscale, so the oval maps to the face
  const side = Math.min(w, h), sx = (w - side) / 2, sy = (h - side) / 2;
  ctx.drawImage(src, sx, sy, side, side, 0, 0, 64, 64);
  const d = ctx.getImageData(0, 0, 64, 64).data;
  return skinToneFromPixels(d, 64, 64);
}

export default function AvatarCreatorModal({ myUid, profile, onClose }) {
  const editing = hasAvatar(profile);
  const [step, setStep] = useState(editing ? 'build' : 'who');
  const [spec, setSpec] = useState(() => (editing ? normalizeSpec(profile.avatar.spec) : defaultSpec('f')));
  const [catalog, setCatalog] = useState(null);
  const [tab, setTab] = useState('pose');
  const [words, setWords] = useState('');
  const [dressing, setDressing] = useState(false);
  const [wordsNote, setWordsNote] = useState('');
  const [camState, setCamState] = useState('idle'); // idle | on | denied
  const [countdown, setCountdown] = useState(null);
  const [note, setNote] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const fileRef = useRef(null);
  const portraitRef = useRef(null);
  const busy = useRef(false);

  useEffect(() => {
    let live = true;
    getCatalog().then((c) => { if (live) setCatalog(c); }).catch((e) => { if (live) setErr(String((e && e.message) || e)); });
    return () => { live = false; };
  }, []);
  // the body downloads while the person is still choosing
  useEffect(() => { if (step !== 'build') warmSpec(spec).catch(() => {}); }, [spec.gender, spec.face, step]); // eslint-disable-line react-hooks/exhaustive-deps

  const patch = useCallback((p) => setSpec((s) => normalizeSpec({ ...s, ...p })), []);

  /* ─── the camera ─── */
  useEffect(() => {
    if (step !== 'selfie') return undefined;
    let cancelled = false;
    (async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'user', width: { ideal: 960 }, height: { ideal: 720 } }, audio: false });
        if (cancelled) { stream.getTracks().forEach((t) => t.stop()); return; }
        streamRef.current = stream;
        if (videoRef.current) { videoRef.current.srcObject = stream; await videoRef.current.play().catch(() => {}); }
        setCamState('on');
      } catch { setCamState('denied'); }
    })();
    return () => {
      cancelled = true;
      if (streamRef.current) { streamRef.current.getTracks().forEach((t) => t.stop()); streamRef.current = null; }
    };
  }, [step]);

  /* the model first, this browser second — see the phone's faceScan.ts */
  const finishShot = useCallback(async (src, w, h, mirrored) => {
    setScanning(true);
    let matched = false;
    try {
      const reading = await readFace(frameDataUrl(src, w, h, mirrored));
      if (reading) { setSpec((s) => applyFaceReading(s, reading)); matched = true; }
    } catch {}
    if (!matched) {
      const tone = toneFromSource(src, w, h, mirrored);
      if (tone) { patch({ skin: nearestSwatch(tone, SKIN_TONES) }); setNote("We couldn't read the face, so we matched your skin tone instead."); }
      else setNote("Couldn't find a face in the photo — pick a tone in the builder.");
    } else {
      setNote('Face matched from your photo.');
    }
    setScanning(false);
    setStep('build');
  }, [patch]);

  const takeShot = useCallback(async () => {
    if (busy.current || !videoRef.current) return;
    busy.current = true;
    try {
      for (let n = 3; n >= 1; n--) { setCountdown(n); await new Promise((r) => setTimeout(r, 650)); } // eslint-disable-line no-await-in-loop
      setCountdown(null);
      const v = videoRef.current;
      await finishShot(v, v.videoWidth || 640, v.videoHeight || 480, true);
    } finally { busy.current = false; setCountdown(null); }
  }, [finishShot]);

  const onUpload = useCallback((file) => {
    if (!file) return;
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => { finishShot(img, img.naturalWidth, img.naturalHeight, false).finally(() => URL.revokeObjectURL(url)); };
    img.onerror = () => { URL.revokeObjectURL(url); setStep('build'); };
    img.src = url;
  }, [finishShot]);

  /* ─── build ─── */
  const docNow = useMemo(() => previewDoc(spec, Date.now()), [spec]);
  const styles = useCallback((kind) => (catalog ? stylesAvailable(catalog, kind, spec.gender, spec.face) : []), [catalog, spec.gender, spec.face]);

  /* ─── result ─── */
  const useAvatar = useCallback(async () => {
    if (!myUid || saving) return;
    setSaving(true); setErr(null);
    try {
      const png = portraitRef.current && (await portraitRef.current.snapshot());
      if (!png) throw new Error("Couldn't render the profile picture.");
      await saveAvatarToProfile(myUid, profile, spec, png);
      onClose && onClose();
    } catch (e) { setErr(String((e && e.message) || e)); }
    finally { setSaving(false); }
  }, [myUid, saving, profile, spec, onClose]);

  const Chips = ({ values, value, onPick, none }) => (
    <div className="av3-chips">
      {(none ? ['none', ...values] : values).map((v) => (
        <button key={v} type="button" className={`av3-chip${v === value ? ' on' : ''}`} onClick={() => onPick(v)}>{STYLE_LABEL[v] || v}</button>
      ))}
    </div>
  );
  const Swatches = ({ values, value, onPick, labels }) => (
    <div className="av3-swatches">
      {values.map((v) => (
        <button key={v} type="button" className={`av3-swatch${String(v).toUpperCase() === String(value).toUpperCase() ? ' on' : ''}`} style={{ background: labels ? labels[v] : v }} onClick={() => onPick(v)} aria-label={v} />
      ))}
    </div>
  );
  const Steps = ({ value, onPick, names }) => (
    <div className="av3-chips av3-steps">
      {BUILD_STEPS.map((v, i) => (
        <button key={v} type="button" className={`av3-chip${Math.abs(v - value) < 0.01 ? ' on' : ''}`} onClick={() => onPick(v)}>{i === 0 ? names[0] : i === 2 ? names[1] : i === 4 ? names[2] : '·'}</button>
      ))}
    </div>
  );

  const head = (title, onBack) => (
    <div className="modal-head av3-head">
      {onBack ? <button type="button" className="icon-btn" onClick={onBack} aria-label="Back">‹</button> : <span />}
      <strong>{title}</strong>
      <button type="button" className="icon-btn" onClick={onClose} aria-label="Close"><X size={20} /></button>
    </div>
  );

  let body;
  if (step === 'who') {
    body = (
      <>
        {head('Your avatar')}
        <div className="modal-body av3-body">
          <h3>Who is this avatar for?</h3>
          <p>Avatars are built on a body type. Pick the one that fits you; everything else — look, hair, clothes — is yours to choose next.</p>
          {[['f', 'Woman'], ['m', 'Man']].map(([g, label]) => (
            <button key={g} type="button" className={`av3-card${spec.gender === g ? ' on' : ''}`} onClick={() => setSpec(defaultSpec(g, spec.face))}>
              <span>{label}</span>{spec.gender === g && <Check size={18} />}
            </button>
          ))}
          <p>Next, one photo can match your skin tone. It's read here in your browser and never uploaded.</p>
          <button type="button" className="btn primary av3-cta" onClick={() => setStep('selfie')}>Continue</button>
          <button type="button" className="btn av3-cta" onClick={() => setStep('build')}>Skip the photo</button>
        </div>
      </>
    );
  } else if (step === 'selfie') {
    body = (
      <>
        {head('Your face', () => setStep('who'))}
        <div className="modal-body av3-body av3-face">
          <div className="av3-cam">
            <video ref={videoRef} playsInline muted autoPlay style={{ transform: 'scaleX(-1)' }} />
            {camState === 'on' && <div className="av3-oval" />}
            {countdown != null && <div className="av3-count">{countdown}</div>}
            {camState === 'denied' && (
              <div className="av3-nocam"><Camera size={36} /><p>The camera isn't available. Upload a photo, or skip it.</p></div>
            )}
          </div>
          <p className="av3-prompt">Look straight at the camera</p>
          <p className="av3-hint">{scanning ? 'Reading your face…' : 'Fit your face in the oval. The photo is read once to shape your avatar, and never stored.'}</p>
          <input ref={fileRef} type="file" accept="image/*" hidden onChange={(e) => { onUpload(e.target.files && e.target.files[0]); e.target.value = ''; }} />
          {camState === 'on' && <button type="button" className="av3-shutter" disabled={countdown != null || scanning} onClick={takeShot} aria-label="Take photo"><span /></button>}
          <button type="button" className="btn av3-cta" onClick={() => fileRef.current && fileRef.current.click()}>Upload a photo</button>
          <button type="button" className="btn av3-cta" onClick={() => setStep('build')}>Skip</button>
        </div>
      </>
    );
  } else if (step === 'build') {
    const skinValues = SKIN_TONES.includes(spec.skin) ? SKIN_TONES : [spec.skin, ...SKIN_TONES];
    body = (
      <>
        {head('Style your avatar', editing ? null : () => setStep('who'))}
        {!!(note || err) && <div className="av3-note">{err || note}</div>}
        <div className="modal-body av3-body av3-build">
          <div className="av3-stage"><Avatar3D avatar={docNow} mode="fullbody" width={300} height={330} interactive returnAfterMs={0} /></div>
          <div className="av3-tabs">
            {TABS.map((k) => <button key={k} type="button" className={`av3-tab${tab === k ? ' on' : ''}`} onClick={() => setTab(k)}>{TAB_LABEL[k]}</button>)}
          </div>
          <div className="av3-pane">
            {tab === 'pose' && (
              <>
                <div className="av3-label">Pose</div>
                <Chips values={POSE_IDS} value={spec.pose} onPick={(v) => patch({ pose: v })} />
              </>
            )}
            {tab === 'face' && (
              <>
                {FACE_SLIDERS.map((k) => (
                  <React.Fragment key={k}>
                    <div className="av3-label">{(FACE_LABEL[k] || [k])[0]}</div>
                    <Steps
                      value={Number((spec.face3d || {})[k]) || 0}
                      onPick={(v) => patch({ face3d: withFaceDim(spec.face3d, k, v) })}
                      names={[(FACE_LABEL[k] || ['', '-'])[1], 'Average', (FACE_LABEL[k] || ['', '', '+'])[2]]}
                    />
                  </React.Fragment>
                ))}
              </>
            )}
            {tab === 'body' && (
              <>
                <div className="av3-label">Build</div>
                {BODY_SLIDERS.map((k) => (
                  <div key={k}>
                    <div className="av3-label">{(BODY_LABEL[k] || [k])[0]}</div>
                    <Steps
                      value={Number((spec.body3d || {})[k]) || 0}
                      onPick={(v) => patch({ body3d: withBodyDim(spec.body3d, k, v) })}
                      names={[(BODY_LABEL[k] || [k, 'Less', 'More'])[1], 'Average', (BODY_LABEL[k] || [k, 'Less', 'More'])[2]]}
                    />
                  </div>
                ))}
              </>
            )}
            {tab === 'style' && (
              <>
                <div className="av3-label">Describe your style</div>
                <p className="av3-hint">Say it in a few words and we will dress the figure — “black leather jacket, ripped jeans, white trainers”.</p>
                <textarea
                  className="av3-words"
                  value={words}
                  onChange={(e) => setWords(e.target.value)}
                  placeholder="black leather jacket, ripped jeans, white trainers"
                  maxLength={280}
                  rows={3}
                  disabled={dressing}
                />
                <button
                  type="button"
                  className="btn primary"
                  disabled={dressing || !words.trim()}
                  onClick={async () => {
                    setDressing(true); setWordsNote('');
                    const reading = await askOutfit(words, {
                      tops: TOP_STYLES, bottoms: BOTTOM_STYLES, shoes: SHOE_STYLES, hats: HAT_STYLES, hair: HAIR_STYLES,
                    });
                    if (reading) { setSpec((s) => applyOutfit(s, reading)); setWordsNote('Dressed from your description.'); }
                    else setWordsNote("We couldn't turn that into an outfit — try naming the pieces and their colours.");
                    setDressing(false);
                  }}
                >{dressing ? 'Dressing…' : 'Dress me'}</button>
                {!!wordsNote && <p className="av3-hint">{wordsNote}</p>}
              </>
            )}
            {tab === 'look' && (
              <>
                <div className="av3-label">Look</div>
                <div className="av3-chips">{FACES.map((f, i) => <button key={f} type="button" className={`av3-chip${spec.face === f ? ' on' : ''}`} onClick={() => patch({ face: f })}>Look {i + 1}</button>)}</div>
                <div className="av3-label">Build</div>
                <Steps value={spec.build} onPick={(v) => patch({ build: v })} names={['Slim', 'Average', 'Heavy']} />
                <div className="av3-label">Height</div>
                <Steps value={spec.height} onPick={(v) => patch({ height: v })} names={['Short', 'Average', 'Tall']} />
              </>
            )}
            {tab === 'skin' && <Swatches values={skinValues} value={spec.skin} onPick={(v) => patch({ skin: v })} />}
            {tab === 'eyes' && <Swatches values={EYE_COLORS} value={spec.eyes} onPick={(v) => patch({ eyes: v })} labels={EYE_SWATCH} />}
            {tab === 'hair' && (<><Chips values={styles('hair')} value={spec.hair.style} onPick={(v) => patch({ hair: { ...spec.hair, style: v } })} none /><Swatches values={HAIR_COLORS} value={spec.hair.color} onPick={(v) => patch({ hair: { ...spec.hair, color: v } })} /></>)}
            {tab === 'top' && (<><Chips values={styles('top')} value={spec.top.style} onPick={(v) => patch({ top: { ...spec.top, style: v } })} /><Swatches values={CLOTH_COLORS} value={spec.top.color} onPick={(v) => patch({ top: { ...spec.top, color: v } })} /></>)}
            {tab === 'bottom' && (<><Chips values={[...styles('pants'), ...styles('skirt')]} value={spec.bottom.style} onPick={(v) => patch({ bottom: { ...spec.bottom, style: v } })} /><Swatches values={CLOTH_COLORS} value={spec.bottom.color} onPick={(v) => patch({ bottom: { ...spec.bottom, color: v } })} /></>)}
            {tab === 'shoes' && (<><Chips values={styles('shoes')} value={spec.shoes.style} onPick={(v) => patch({ shoes: { ...spec.shoes, style: v } })} /><Swatches values={CLOTH_COLORS} value={spec.shoes.color} onPick={(v) => patch({ shoes: { ...spec.shoes, color: v } })} /></>)}
            {tab === 'hat' && (<><Chips values={styles('hat')} value={(spec.hat && spec.hat.style) || 'none'} onPick={(v) => patch({ hat: v === 'none' ? null : { style: v, color: (spec.hat && spec.hat.color) || CLOTH_COLORS[3] } })} none />{!!spec.hat && <Swatches values={CLOTH_COLORS} value={spec.hat.color} onPick={(v) => patch({ hat: { style: spec.hat.style, color: v } })} />}</>)}
          </div>
          <button type="button" className="btn primary av3-cta" onClick={() => setStep('result')}>Continue</button>
        </div>
      </>
    );
  } else {
    body = (
      <>
        {head('Your avatar', () => setStep('build'))}
        <div className="modal-body av3-body av3-center">
          <Avatar3D avatar={docNow} mode="fullbody" width={300} height={380} interactive returnAfterMs={0} />
          <p>Drag to turn it around.</p>
          <div className="av3-portrait"><Avatar3D ref={portraitRef} avatar={docNow} mode="portrait" size={128} interactive={false} snapshotable style={{ position: 'absolute', left: 0, bottom: 0 }} /></div>
          <p>This is how you'll appear in chats.</p>
          {!!err && <div className="av3-note">{err}</div>}
          <button type="button" className="btn primary av3-cta" disabled={saving} onClick={useAvatar}>{saving ? 'Saving…' : 'Use this avatar'}</button>
        </div>
      </>
    );
  }

  return (
    <div className="overlay" onClick={onClose}>
      <div className="modal av3-modal" onClick={(e) => e.stopPropagation()}>{body}</div>
    </div>
  );
}
